import {
  useCreateApplicationMutation,
  useGetApplicationByCompanyIdQuery,
  useUpdateApplicationMutation,
} from 'src/features/application/api/applicationApi';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { Application } from '@typeRoots/common/application';
import { initialApplicationState } from 'src/features/application/slice/initialState';
import { URLS } from 'src/constants/urls';
import { useLinkTo } from '@react-navigation/native';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';

const boolToYesNo = (val?: boolean | null) => (val === true ? 'Yes' : val === false ? 'No' : '');
const yesNoToBool = (val: string): boolean | null =>
  val === 'Yes' ? true : val === 'No' ? false : null;

export const useInsurance = () => {
  const linkTo = useLinkTo();

  //real time data
  const currentCompany = useSelector((state: RootState) => state.company.company);

  const { data: fetchedData } = useGetApplicationByCompanyIdQuery(currentCompany?.id || 0);
  const [createApplication] = useCreateApplicationMutation();
  const [updateApplication] = useUpdateApplicationMutation();

  const application = useSelector((state: RootState) => state.application.application);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const isApplicationAlreadyCreated = useSelector(
    (state: RootState) => state.application.applicationCreated,
  );

  //autoliability card states
  const [autoLiability, setAutoLiability] = useState<'opt-in' | 'opt-out' | null>(null);
  const [autoLiabilityLimit, setAutoLiabilityLimit] = useState<number>(1000000);
  const [personalInjuryProtection, setPersonalInjuryProtection] = useState<
    'included' | 'not-included' | null
  >(null);
  const [pipLimit, setPipLimit] = useState<string>('');
  const [uninsuredMotorist, setUninsuredMotorist] = useState<'included' | 'not-included' | null>(
    null,
  );
  const [umLimit, setUmLimit] = useState<string>('');
  const [underinsuredMotorist, setUnderinsuredMotorist] = useState<
    'included' | 'not-included' | null
  >(null);
  const [uimLimit, setUimLimit] = useState<string>('');

  // physical damage card states
  const [physicalDamage, setPhysicalDamage] = useState<'per-vehicle' | 'opt-out' | null>(null);
  const [hasTrailerInterchangeAgreement, setHasTrailerInterchangeAgreement] = useState<string>('');
  const [uiiaIntermodal, setUiiaIntermodal] = useState<string>('');
  const [physicalDamageDeductible, setPhysicalDamageDeductible] = useState<number>(1000);
  const [collisionDeductible, setCollisionDeductible] = useState<number>(1000);
  const [trailerInterchangeLimit, setTrailerInterchangeLimit] = useState<string>('30000');
  const [towingLaborStorage, setTowingLaborStorage] = useState<number>(2500);

  // motor truck cargo card states
  const [motorTruckCargo, setMotorTruckCargo] = useState<'opt-in' | 'opt-out' | null>(null);
  const [motorTruckCargoLimit, setMotorTruckCargoLimit] = useState<number>(100000);
  const [motorTruckDeductible, setMotorTruckDeductible] = useState<number>(1000);
  const [reeferBreakdown, setReeferBreakdown] = useState<'opt-in' | 'opt-out'>('opt-out');
  const [reeferBreakdownLimit, setReeferBreakdownLimit] = useState<number>(50000);
  const [reeferBreakdownDeductible, setReeferBreakdownDeductible] = useState<number>(1000);

  // general liability card states
  const [generalLiability, setGeneralLiability] = useState<'opt-in' | 'opt-out' | null>('opt-in');
  const [generalLiabilityLimit, setGeneralLiabilityLimit] = useState<number>(1000000);

  // non trucking liability card states
  const [nonTruckingLiability, setNonTruckingLiability] = useState<'opt-in' | 'opt-out' | null>(
    null,
  );
  const [nonTruckingLiabilityLimit, setNonTruckingLiabilityLimit] = useState<number>(1000000);

  const [insuranceSaveErrorToast, setInsuranceSaveErrorToast] = useState(false);

  // google tag event for screen landed
  useEffect(() => {
    trackGTMEvent(
      'lob-page-landed',
      {
        userEvent: 'User landed on the LOB (Insurance)',
      },
      accountType,
    );
  }, []);

  //wireup conditions
  const updateState = (
    condition: boolean,
    setFn: Function,
    optInValue: string,
    optOutValue: string,
  ) => {
    setFn(condition ? optInValue : optOutValue);
  };

  useEffect(() => {
    if (application) {
      // Auto Liability
      const autoLimit = application.autoLiabilityLimitRequested?.amount || 1000000;
      const autoLiabilityRequested = application.autoLiabilityRequested;
      setAutoLiabilityLimit(autoLimit);
      updateState(autoLiabilityRequested === true, setAutoLiability, 'opt-in', 'opt-out');

      // Personal Injury Protection
      updateState(
        !!application.personalInjuryProtectionRequested,
        setPersonalInjuryProtection,
        'included',
        'not-included',
      );

      // PIP limit
      if (application.pipLimit) setPipLimit(String(application.pipLimit));

      // Uninsured Motorist
      updateState(
        !!application.uninsuredMotoristRequested,
        setUninsuredMotorist,
        'included',
        'not-included',
      );
      if (application.umLimit) setUmLimit(String(application.umLimit));

      // Underinsured Motorist
      updateState(
        !!application.underinsuredMotoristRequested,
        setUnderinsuredMotorist,
        'included',
        'not-included',
      );
      if (application.uimLimit) setUimLimit(String(application.uimLimit));

      // Physical Damage
      // Prefer comprehensiveDeductible (producer-specific) over the generic deductible when
      // both exist; avoids overwriting a previously saved comp value on next save.
      const deductible =
        application.comprehensiveDeductible?.amount || application.deductible?.amount || 1000;
      const automobilePhysicalDamageRequested = application.automobilePhysicalDamageRequested;
      setPhysicalDamageDeductible(deductible);
      if (application.collisionDeductible?.amount)
        setCollisionDeductible(application.collisionDeductible.amount);
      updateState(
        automobilePhysicalDamageRequested === true,
        setPhysicalDamage,
        'per-vehicle',
        'opt-out',
      );

      // Trailer Interchange Limit
      const trailerLimit = application.trailerInterchangeLimitRequested?.amount || 0;
      const tailerLimitRequested = application.trailerInterchangeRequested;
      if (tailerLimitRequested && trailerLimit > 0) {
        setTrailerInterchangeLimit(String(trailerLimit));
      } else {
        setTrailerInterchangeLimit('opt-out');
      }
      setHasTrailerInterchangeAgreement(boolToYesNo(application.hasTrailerInterchangeAgreement));
      setUiiaIntermodal(boolToYesNo(application.uiiaIntermodal));

      // Towing Labor and Storage
      setTowingLaborStorage(application.towingStorageLimitRequested?.amount || 10000);

      // Motor Truck Cargo
      const motorTruckLimit = application.motorTruckCargoLimitRequested?.amount || 100000;
      const motorTruckCargoRequested = application.motorTruckCargoRequested;
      setMotorTruckCargoLimit(motorTruckLimit);

      const motorTruckDeductible = application.motorTruckDeductibleRequested?.amount || 1000;
      setMotorTruckDeductible(motorTruckDeductible);
      updateState(motorTruckCargoRequested === true, setMotorTruckCargo, 'opt-in', 'opt-out');
      updateState(
        application.reeferBreakdownRequested === true,
        setReeferBreakdown,
        'opt-in',
        'opt-out',
      );
      if (application.reeferBreakdownLimit) {
        setReeferBreakdownLimit(application.reeferBreakdownLimit);
      }
      if (application.reeferBreakdownDeductible) {
        setReeferBreakdownDeductible(application.reeferBreakdownDeductible);
      }

      // General Liability
      updateState(
        !!application.truckingGeneralInsuranceRequested,
        setGeneralLiability,
        'opt-in',
        'opt-out',
      );

      // General Trucking Liability Limit
      const generalLiabilityLimit =
        application.truckingGeneralInsuranceLimitRequested?.amount || 1000000;
      const truckingGeneralInsuranceRequested = application.truckingGeneralInsuranceRequested;
      setGeneralLiabilityLimit(generalLiabilityLimit);
      updateState(
        truckingGeneralInsuranceRequested === true,
        setGeneralLiability,
        'opt-in',
        'opt-out',
      );

      // Non-Trucking Liability
      updateState(
        !!application.nonTruckingLiabilityRequested,
        setNonTruckingLiability,
        'opt-in',
        'opt-out',
      );

      const nonTruckingLiabilityRequested = application.nonTruckingLiabilityRequested;
      updateState(
        nonTruckingLiabilityRequested === true,
        setNonTruckingLiability,
        'opt-in',
        'opt-out',
      );
    }
  }, [fetchedData, application]);

  //Application data after it is edited in the UI
  let applicationData = initialApplicationState;
  if (isApplicationAlreadyCreated) {
    applicationData = application as Application;
  }

  const isAutoLiabilitySelected = autoLiability === 'opt-in';
  const isPersonalInjuryProtectionSelected = personalInjuryProtection === 'included';
  const isUninsuredMotoristSelected = uninsuredMotorist === 'included';
  const isPhysicalDamageSelected = physicalDamage === 'per-vehicle';
  const isTrailerInterchangeSelected = parseInt(trailerInterchangeLimit || '0', 10) > 0;
  //currently towing is default 2500, this may change in future
  //const isTowingSelected = towingLaborStorage !== 0 && towingLaborStorage !== null;
  const isMotorTruckCargoSelected = motorTruckCargo === 'opt-in';
  const isReeferBreakdownSelected = reeferBreakdown === 'opt-in';
  const isGeneralLiabilitySelected = generalLiability === 'opt-in';
  const isNonTruckingLiabilitySelected = nonTruckingLiability === 'opt-in';

  const prepareApplicationData = (): Application => ({
    ...applicationData,
    applicationStep: 'LOB',
    // AUOTO LIABILITY
    autoLiabilityRequested: isAutoLiabilitySelected,
    autoLiabilityLimitRequested:
      autoLiabilityLimit && isAutoLiabilitySelected
        ? { amount: autoLiabilityLimit, currency: 'USD' }
        : { amount: 0, currency: 'USD' },

    // Personal Injury Protection
    personalInjuryProtectionRequested:
      isAutoLiabilitySelected && isPersonalInjuryProtectionSelected,
    pipLimit: isPersonalInjuryProtectionSelected && pipLimit ? Number(pipLimit) : undefined,

    // Uninsured Motorist
    uninsuredMotoristRequested: isAutoLiabilitySelected && isUninsuredMotoristSelected,
    umLimit: isUninsuredMotoristSelected && umLimit ? Number(umLimit) : undefined,

    // Underinsured Motorist
    underinsuredMotoristRequested: underinsuredMotorist === 'included',
    uimLimit: underinsuredMotorist === 'included' && uimLimit ? Number(uimLimit) : undefined,

    // PHYSICAL DAMAGE
    automobilePhysicalDamageRequested: isPhysicalDamageSelected,
    deductible: isPhysicalDamageSelected
      ? { amount: physicalDamageDeductible, currency: 'USD' }
      : { amount: 0, currency: 'USD' },

    // The primary "Deductible" (shown as "Deductible Comprehensive" for producers) is the
    // comprehensive value. For non-producers it also fills collision; producers set collision separately.
    comprehensiveDeductible: isPhysicalDamageSelected
      ? { amount: physicalDamageDeductible, currency: 'USD' }
      : { amount: 0, currency: 'USD' },
    collisionDeductible: isPhysicalDamageSelected
      ? {
          amount: accountType === 'producer' ? collisionDeductible : physicalDamageDeductible,
          currency: 'USD',
        }
      : { amount: 0, currency: 'USD' },

    //trailer interchange
    hasTrailerInterchangeAgreement: isTrailerInterchangeSelected
      ? yesNoToBool(hasTrailerInterchangeAgreement)
      : null,
    trailerInterchangeRequested: isPhysicalDamageSelected && isTrailerInterchangeSelected,
    // Producers set UIIA explicitly (grouped under Trailer Interchange); customers keep the
    // derived default (Physical Damage && Trailer Interchange).
    uiiaIntermodal:
      isPhysicalDamageSelected && isTrailerInterchangeSelected
        ? accountType === 'producer'
          ? uiiaIntermodal === 'Yes'
          : true
        : false,
    trailerInterchangeLimitRequested:
      trailerInterchangeLimit === 'opt-out' || !isPhysicalDamageSelected
        ? { amount: 0, currency: 'USD' }
        : { amount: parseInt(trailerInterchangeLimit || '0', 10), currency: 'USD' },

    //towing
    includeAPDTowing: true,
    towingStorageLimitRequested:
      isPhysicalDamageSelected && towingLaborStorage
        ? { amount: towingLaborStorage, currency: 'USD' }
        : { amount: 0, currency: 'USD' },

    // MOTOR TRUCK CARGO
    motorTruckCargoRequested: isMotorTruckCargoSelected,
    motorTruckCargoLimitRequested: isMotorTruckCargoSelected
      ? { amount: motorTruckCargoLimit, currency: 'USD' }
      : { amount: 0, currency: 'USD' },
    motorTruckDeductibleRequested: isMotorTruckCargoSelected
      ? { amount: motorTruckDeductible, currency: 'USD' }
      : { amount: 0, currency: 'USD' },
    // Reefer breakdown is a producer-only sub-option of Motor Truck Cargo
    reeferBreakdownRequested: isMotorTruckCargoSelected && isReeferBreakdownSelected,
    reeferBreakdownLimit:
      isMotorTruckCargoSelected && isReeferBreakdownSelected ? reeferBreakdownLimit : undefined,
    reeferBreakdownDeductible:
      isMotorTruckCargoSelected && isReeferBreakdownSelected
        ? reeferBreakdownDeductible
        : undefined,

    // GENERAL LIABILITY
    truckingGeneralInsuranceRequested: isGeneralLiabilitySelected,
    truckingGeneralInsuranceLimitRequested:
      generalLiabilityLimit && isGeneralLiabilitySelected
        ? { amount: generalLiabilityLimit, currency: 'USD' }
        : { amount: 0, currency: 'USD' },

    // NON-TRUCKING LIABILITY
    nonTruckingLiabilityRequested: isNonTruckingLiabilitySelected,

    annualMileage: vehicles.length * 70000,
  });

  //handleSaveOrUpdate
  const handleSaveOrUpdate = async () => {
    if (isApplicationAlreadyCreated) {
      try {
        const applicationData = prepareApplicationData();
        await updateApplication({
          id: application?.id as number,
          updatedData: { ...applicationData, company: { id: currentCompany?.id || 0 } },
        }).unwrap();
        linkTo(URLS.APPLICATION.POLICY);
      } catch (err) {
        setInsuranceSaveErrorToast(true);
        toggleToastState(setInsuranceSaveErrorToast);
        console.error('Error updating aplication:', err);
      }
    } else {
      try {
        const applicationData = prepareApplicationData();
        await createApplication({
          ...applicationData,
          company: { id: currentCompany?.id || 0 },
        }).unwrap();
        linkTo(URLS.APPLICATION.POLICY);
      } catch (err) {
        setInsuranceSaveErrorToast(true);
        toggleToastState(setInsuranceSaveErrorToast);
        console.error('Error saving aplication:', err);
      }
    }
  };

  return {
    handleSaveOrUpdate,
    insuranceSaveErrorToast,
    autoLiability: {
      value: autoLiability,
      set: setAutoLiability,
      limit: autoLiabilityLimit,
      setLimit: setAutoLiabilityLimit,
      personalInjuryProtection: {
        value: personalInjuryProtection,
        set: setPersonalInjuryProtection,
        limit: pipLimit,
        setLimit: setPipLimit,
      },
      uninsuredMotorist: {
        value: uninsuredMotorist,
        set: setUninsuredMotorist,
        limit: umLimit,
        setLimit: setUmLimit,
      },
      underinsuredMotorist: {
        value: underinsuredMotorist,
        set: setUnderinsuredMotorist,
        limit: uimLimit,
        setLimit: setUimLimit,
      },
    },
    isProducer: accountType === 'producer',
    physicalDamage: {
      value: physicalDamage,
      set: setPhysicalDamage,
      deductible: {
        value: physicalDamageDeductible,
        set: setPhysicalDamageDeductible,
      },
      collisionDeductible: {
        value: collisionDeductible,
        set: setCollisionDeductible,
      },
      trailerInterchangeLimit: {
        value: trailerInterchangeLimit,
        set: setTrailerInterchangeLimit,
      },
      hasTrailerInterchangeAgreement: {
        value: hasTrailerInterchangeAgreement,
        set: setHasTrailerInterchangeAgreement,
      },
      uiiaIntermodal: {
        value: uiiaIntermodal,
        set: setUiiaIntermodal,
      },
      towingLaborStorage: {
        value: towingLaborStorage,
        set: setTowingLaborStorage,
      },
    },
    motorTruckCargo: {
      value: motorTruckCargo,
      set: setMotorTruckCargo,
      limit: motorTruckCargoLimit,
      setLimit: setMotorTruckCargoLimit,
      deductible: {
        value: motorTruckDeductible,
        set: setMotorTruckDeductible,
      },
      reeferBreakdown: {
        value: reeferBreakdown,
        set: setReeferBreakdown,
        limit: reeferBreakdownLimit,
        setLimit: setReeferBreakdownLimit,
        deductible: {
          value: reeferBreakdownDeductible,
          set: setReeferBreakdownDeductible,
        },
      },
    },
    generalLiability: {
      value: generalLiability,
      set: setGeneralLiability,
      limit: generalLiabilityLimit,
      setLimit: setGeneralLiabilityLimit,
    },
    nonTruckingLiability: {
      value: nonTruckingLiability,
      set: setNonTruckingLiability,
      limit: nonTruckingLiabilityLimit,
      setLimit: setNonTruckingLiabilityLimit,
    },
  };
};
